// src/hooks/useOffline.js — connection + pending-sync state for the UI.

import { useEffect, useState, useCallback } from 'react';
import { subscribe, getAll, drain, retryOp, discard, isPausedForAuth, OP_FAILED } from '../services/outbox';

/**
 * Live view of whether we're online and what's still waiting to sync.
 *
 * `online` starts from navigator.onLine, which is optimistic on mobile — it
 * reports true for a connection that can't actually reach anything. The queue
 * itself is the honest signal, so treat `pending > 0` as the thing to show.
 */
export function useOffline() {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );
  const [ops, setOps] = useState([]);

  useEffect(() => {
    let alive = true;
    getAll().then((list) => alive && setOps(list));
    const unsub = subscribe((list) => alive && setOps(list));

    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      alive = false;
      unsub();
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const pending = ops.filter((o) => o.status !== OP_FAILED);
  const failed = ops.filter((o) => o.status === OP_FAILED);

  return {
    online,
    ops,
    pending,
    failed,
    pendingCount: pending.length,
    failedCount: failed.length,
    // True when the queue is held because the session expired — usually a backend
    // restart. The fix is signing back in, not retrying.
    needsLogin: isPausedForAuth(),
    syncNow: useCallback(() => drain(), []),
    retryOp: useCallback((id) => retryOp(id), []),
    discardOp: useCallback((id) => discard(id), []),
  };
}

/**
 * Pending ops of a given kind, for optimistic overlays — e.g. showing a crew as
 * clocked in while their clock-in is still queued.
 */
export function usePendingOps(kind, jobId = null) {
  const { pending } = useOffline();
  return pending.filter(
    (op) => (!kind || op.kind === kind) && (jobId == null || op.jobId === jobId)
  );
}

export default useOffline;
